import React, { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/**
 * PostReactions — thumbs up / thumbs down with counts.
 * - Toggling same kind clears the reaction.
 * - Switching kind moves the count atomically.
 * - Counts use tabular-nums for stability.
 * - Has NO effect on feed ordering (chronology-first stays).
 */
export default function PostReactions({
  postId,
  initial = { kind: null, like_count: 0, dislike_count: 0 },
  size = 'sm',
  className = '',
}) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState({
    kind: initial.kind || null,
    like_count: Number(initial.like_count || 0),
    dislike_count: Number(initial.dislike_count || 0),
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setState((s) => ({
      ...s,
      like_count: Number(initial.like_count || 0),
      dislike_count: Number(initial.dislike_count || 0),
      kind: initial.kind || null,
    }));
    // eslint-disable-next-line
  }, [postId, initial.kind, initial.like_count, initial.dislike_count]);

  // Lazily hydrate `my_reaction` if not provided by the feed.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (initial.kind !== undefined) return; // already provided
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/posts/${postId}/reactions/me`);
        if (!cancelled) setState((s) => ({ ...s, kind: data?.kind || null }));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [postId, isAuthenticated, initial.kind]);

  const react = async (kind) => {
    if (!isAuthenticated) { toast.error('Sign in to react'); return; }
    if (busy) return;
    setBusy(true);
    // Optimistic update
    setState((s) => {
      let { like_count, dislike_count, kind: cur } = s;
      if (cur === kind) {
        if (kind === 'like') like_count = Math.max(0, like_count - 1);
        else dislike_count = Math.max(0, dislike_count - 1);
        cur = null;
      } else if (cur && cur !== kind) {
        if (cur === 'like') like_count = Math.max(0, like_count - 1);
        if (cur === 'dislike') dislike_count = Math.max(0, dislike_count - 1);
        if (kind === 'like') like_count += 1;
        else dislike_count += 1;
        cur = kind;
      } else {
        if (kind === 'like') like_count += 1;
        else dislike_count += 1;
        cur = kind;
      }
      return { ...s, like_count, dislike_count, kind: cur };
    });
    try {
      const { data } = await api.post(`/posts/${postId}/reactions`, { kind });
      setState({
        kind: data?.kind || null,
        like_count: Number(data?.like_count || 0),
        dislike_count: Number(data?.dislike_count || 0),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reaction failed');
      // best-effort refresh
      try {
        const { data } = await api.get(`/posts/${postId}/reactions/me`);
        setState((s) => ({ ...s, kind: data?.kind || null }));
      } catch {}
    } finally { setBusy(false); }
  };

  const upActive = state.kind === 'like';
  const downActive = state.kind === 'dislike';
  const sm = size === 'sm';
  const padY = sm ? 'py-1' : 'py-1.5';
  const padX = sm ? 'px-2.5' : 'px-3';
  const iconSz = sm ? 'h-4 w-4' : 'h-[18px] w-[18px]';

  return (
    <div className={`inline-flex items-stretch gap-1 ${className}`} data-testid="post-reactions">
      <button
        type="button"
        onClick={() => react('like')}
        disabled={busy}
        aria-pressed={upActive}
        className={`group inline-flex items-center gap-1.5 rounded-full border ${padX} ${padY} text-xs font-mono transition-colors
          ${upActive
            ? 'border-[hsl(var(--success)/0.45)] bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))]'
            : 'border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] text-muted-foreground hover:border-[hsl(var(--accent)/0.40)] hover:bg-[hsl(var(--accent)/0.08)] hover:text-foreground'}
        `}
        data-testid="post-reaction-thumbs-up-button"
      >
        <ThumbsUp className={iconSz} />
        <span className="tabular-nums" data-testid="post-reaction-thumbs-up-count">{state.like_count}</span>
      </button>
      <button
        type="button"
        onClick={() => react('dislike')}
        disabled={busy}
        aria-pressed={downActive}
        className={`group inline-flex items-center gap-1.5 rounded-full border ${padX} ${padY} text-xs font-mono transition-colors
          ${downActive
            ? 'border-[hsl(var(--danger)/0.45)] bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]'
            : 'border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] text-muted-foreground hover:border-[hsl(var(--accent)/0.40)] hover:bg-[hsl(var(--accent)/0.08)] hover:text-foreground'}
        `}
        data-testid="post-reaction-thumbs-down-button"
      >
        <ThumbsDown className={iconSz} />
        <span className="tabular-nums" data-testid="post-reaction-thumbs-down-count">{state.dislike_count}</span>
      </button>
    </div>
  );
}
